import { Stack } from '@chakra-ui/react'
import { InputFormControl, SwitchFormControl, TextareaFormControl } from 'BrokerTsLib/src/components'
import type { EditOrganizationUnit } from 'BrokerTsLib/src/dtos'
import { Insurer, OrganizationUnitType } from 'BrokerTsLib/src/enums'
import { useFormContext } from 'BrokerTsLib/src/rhf'
import type { Ref } from 'react'
import { forwardRef } from 'react'

import type { FormSectionProps } from '@/containers/forms/FormSection'
import { FormSection } from '@/containers/forms/FormSection'
import { useAuth } from '@/context/AuthContext'
import { usePartner } from '@/context/PartnerContext'
import { useGetAvailableInsurers } from 'BrokerTsLib/src/hooks/useOrganizationUnit'

export const OrganizationAppSettingsFormSection = forwardRef(
  function OrganizationAppSettingsFormSection(props: FormSectionProps, ref: Ref<HTMLDivElement>) {
    const { control, watch } = useFormContext<EditOrganizationUnit>()
    const { currentPartner } = usePartner()
    const partner = currentPartner?.partner
    const { user: authUser } = useAuth()

    const ouType = watch('type')
    const showGcpPartnerSettings = partner === 'Kapitol' && ouType === OrganizationUnitType.GcpPartner

    const ouId = watch('id')
    const superiorOuId = watch('superiorOrganizationUnitId')
    const { data } = useGetAvailableInsurers(ouId ?? superiorOuId)
    const isGcpAllowed = data?.data?.some(insurer => insurer.insurerId === Insurer.GeneraliCeskaPojistovna)

    return <FormSection ref={ref} sectionTitle='Nastavení aplikace'>
      <Stack gap={6}>
        {showGcpPartnerSettings &&
          <InputFormControl label="Copyright"
            placeholder="Zobrazuje se např. v zápatí tištěných nabídek"
            control={control} name="properties.copyright" />
        }

        <InputFormControl label="Webové stránky"
          placeholder="Např. https://www.cebia.cz/"
          control={control} name="properties.webPage" />

        <TextareaFormControl label="E-mail - zápatí"
          control={control} name="properties.footerInfo" />

        <TextareaFormControl label="Poučení subjektu - záhlaví"
          placeholder="Napište poučení subjektu, které se zobrazí v záhlaví."
          control={control} name="properties.informationMemorandumHeader" />

        <TextareaFormControl label="Poučení subjektu - zápatí"
          placeholder="Napište poučení subjektu, které se zobrazí v zápatí."
          control={control} name="properties.informationMemorandumBottom" />

        <TextareaFormControl label="Obchodní rejstřík"
          placeholder="Poznámka o zápisu firmy do obchodního rejstříku"
          control={control} name="properties.businessRegister" />

        <SwitchFormControl name="properties.businessSubject" control={control} label="Podnikatelský subjekt" />

        <SwitchFormControl name="properties.policyHolderEmailRequired" control={control} label="Povinné vyplnění emailu pojistníka" />

        {partner === 'Kapitol' && <>
          <SwitchFormControl name='properties.preferKoopOffers' control={control} label="Preference Kooperativy při řazení nabídek" />
          <SwitchFormControl name='properties.subaruInsurance' control={control} label="Značkové pojištění SUBARU" />
          <SwitchFormControl name='properties.mazdaInsurance' control={control} label="Značkové pojištění MAZDA" />
          <SwitchFormControl name='properties.kiaInsurance' control={control} label="Značkové pojištění KIA" />
          <SwitchFormControl name='properties.mgInsurance' control={control} label="Značkové pojištění MG" />

          <SwitchFormControl name='properties.generateOffersWithSpecRisks' control={control} label="Generovat vyšší asistenci u vybraných pojišťoven" />
        </>}

        {(authUser?.allowGenerateKoopSalon) &&
          <SwitchFormControl name='properties.generateKoopSalonOffers' control={control} label="Generovat nabídky KOOPsalon" />
        }

        {(partner === 'Jap' && authUser?.isSupervisor) &&
          <SwitchFormControl name='properties.isCallCentrum' control={control} label="Přístup k Poleasing nabídkám (CallCentrum)" />
        }

        {partner === 'Essox' &&
          <SwitchFormControl name="properties.poleasRights" control={control} label="Poleasing" />
        }

        {isGcpAllowed &&
          <SwitchFormControl name='properties.allowGcpDigitalSignature' control={control} label="Povolit digitální podpis GČP" defaultChecked={true} />
        }
      </Stack>
    </FormSection>
  }
)